import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getPlanDisplayName } from "@/lib/planUtils";
import { CreditosModal } from "@/components/planos/CreditosModal";
import { AssinaturaModal } from "@/components/planos/AssinaturaModal";
import { useMercadoPago } from "@/hooks/useMercadoPago";

const PlanosPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createPreference, isLoading } = useMercadoPago();
  const [isCompany, setIsCompany] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [creditosModalOpen, setCreditosModalOpen] = useState(false);
  const [assinaturaModalOpen, setAssinaturaModalOpen] = useState(false);
  const [startPlanLoading, setStartPlanLoading] = useState(false);

  const creditosAvulsos = [
    { creditos: 1, valor: "R$ 120,00", valorPorVaga: "R$ 120" },
    { creditos: 3, valor: "R$ 330,00", valorPorVaga: "R$ 110" },
    { creditos: 5, valor: "R$ 500,00", valorPorVaga: "R$ 100" },
    { creditos: 10, valor: "R$ 900,00", valorPorVaga: "R$ 90" },
  ];

  const planoProfissional = [
    { vagas: 5, valor: "R$ 220,00" },
    { vagas: 10, valor: "R$ 275,00" },
    { vagas: 20, valor: "R$ 330,00" },
    { vagas: 30, valor: "R$ 415,00" },
    { vagas: 50, valor: "R$ 550,00" },
    { vagas: 100, valor: "R$ 880,00" },
    { vagas: 200, valor: "R$ 1.500,00" },
  ];

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);

      if (session) {
        const { data: companyProfile } = await supabase
          .from("company_profiles")
          .select("id")
          .eq("user_id", session.user.id)
          .single();

        setIsCompany(!!companyProfile);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setIsAuthenticated(!!session);

      if (session) {
        const { data: companyProfile } = await supabase
          .from("company_profiles")
          .select("id")
          .eq("user_id", session.user.id)
          .single();

        setIsCompany(!!companyProfile);
      } else {
        setIsCompany(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleStartClick = async () => {
    if (!isAuthenticated || !isCompany) {
      navigate("/entrar");
      return;
    }

    setStartPlanLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/entrar");
        return;
      }

      const { data: companyProfile, error: profileError } = await supabase
        .from("company_profiles")
        .select("id, plan")
        .eq("user_id", session.user.id)
        .single();

      if (profileError || !companyProfile) {
        toast({
          title: "Erro",
          description: "Não foi possível encontrar seu perfil de empresa.",
          variant: "destructive",
        });
        return;
      }

      if (companyProfile.plan && companyProfile.plan !== "gratuito" && companyProfile.plan !== "start") {
        toast({
          title: "Você já possui um plano",
          description: `Seu plano atual é: ${getPlanDisplayName(companyProfile.plan)}. Gerencie seu plano na área de conta.`,
        });
        return;
      }

      const { error: updateError } = await supabase
        .from("company_profiles")
        .update({ plan: "start" })
        .eq("id", companyProfile.id);

      if (updateError) {
        toast({
          title: "Erro",
          description: "Não foi possível ativar o plano. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Plano Start Ativado!",
        description: "Você pode publicar 1 vaga gratuitamente por 30 dias.",
      });

      navigate("/empresa/dashboard");
    } catch (error) {
      console.error("Error activating start plan:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setStartPlanLoading(false);
    }
  };

  const handleCreditosClick = () => {
    if (!isAuthenticated) {
      navigate("/entrar");
      return;
    }
    setCreditosModalOpen(true);
  };

  const handleAssinaturaClick = () => {
    if (!isAuthenticated) {
      navigate("/entrar");
      return;
    }
    setAssinaturaModalOpen(true);
  };

  const handleCreditosConfirm = async (creditsAmount: number) => {
    await createPreference({
      type: "credits",
      credits_amount: creditsAmount,
    });
  };

  const handleAssinaturaConfirm = async (jobsPerMonth: number) => {
    await createPreference({
      type: "subscription",
      jobs_per_month: jobsPerMonth,
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <section className="py-12 bg-muted/30">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <Badge variant="featured" className="mb-4">Planos e Preços</Badge>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
                Escolha o plano ideal para sua empresa
              </h1>
              <p className="text-lg text-muted-foreground">
                Comece gratuitamente e faça upgrade quando precisar de mais recursos
              </p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container">
            <div className="mobile-container md:w-full max-w-7xl md:mx-auto flex flex-col lg:flex-row lg:items-start gap-6">
              
              {/* Card 1 - Plano Start (Gratuito) */}
              <Card className="lg:flex-1">
                <CardHeader>
                  <Badge variant="secondary" className="w-fit mb-2">Entrada</Badge>
                  <CardTitle className="text-2xl font-display">Plano Start</CardTitle>
                  <CardDescription className="text-base">
                    Comece sem custo e valide a JobwayX na prática
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">R$ 0,00</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    Ideal para empresas que querem conhecer a plataforma e entender como a JobwayX melhora a qualidade dos candidatos.
                  </p>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>1 vaga ativa por 30 dias</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>Divulgação básica</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>Painel de gerenciamento</span>
                    </li>
                  </ul>

                  <Separator />

                  <p className="text-sm text-muted-foreground italic">
                    Publique, acompanhe e veja a diferença na organização dos candidatos — sem compromisso.
                  </p>

                  <Button 
                    className="w-full" 
                    size="lg" 
                    onClick={handleStartClick}
                    disabled={startPlanLoading}
                  >
                    {startPlanLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Ativando...
                      </>
                    ) : (
                      "Começar Grátis"
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Card 2 - Créditos Avulsos */}
              <Card className="lg:flex-1">
                <CardHeader>
                  <Badge variant="accent" className="w-fit mb-2">Flexibilidade</Badge>
                  <CardTitle className="text-2xl font-display">Créditos Avulsos</CardTitle>
                  <CardDescription className="text-base">
                    Para quem contrata pontualmente, mas não aceita errar
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    Publique vagas quando precisar, com inteligência, organização e triagem que reduzem retrabalho.
                  </p>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Validade: 90 dias</Badge>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Créditos</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Valor por vaga</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creditosAvulsos.map((item) => (
                          <TableRow key={item.creditos}>
                            <TableCell className="font-medium">{item.creditos} crédito{item.creditos > 1 ? 's' : ''}</TableCell>
                            <TableCell>{item.valor}</TableCell>
                            <TableCell>{item.valorPorVaga}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>Publicação confidencial</span>
                    </li>
                  </ul>

                  <Separator />

                  <p className="text-sm font-medium text-foreground">
                    Menos currículos irrelevantes. Mais clareza na escolha.
                  </p>

                  <Button 
                    className="w-full" 
                    variant="outline" 
                    size="lg" 
                    onClick={handleCreditosClick}
                    disabled={isLoading}
                  >
                    Comprar Créditos
                  </Button>
                </CardContent>
              </Card>

              {/* Card 3 - Plano Profissional (Assinatura) */}
              <Card className="lg:flex-1 border-primary shadow-lg">
                <CardHeader>
                  <Badge variant="featured" className="w-fit mb-2">Recomendado</Badge>
                  <CardTitle className="text-2xl font-display">Plano Profissional</CardTitle>
                  <CardDescription className="text-base">
                    Recrutamento contínuo com inteligência e suporte
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    Para empresas que contratam com frequência e precisam ganhar tempo, reduzir erros e manter controle do processo seletivo.
                  </p>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Faturamento mensal recorrente</Badge>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vagas/mês</TableHead>
                          <TableHead>Valor mensal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {planoProfissional.map((item) => (
                          <TableRow key={item.vagas}>
                            <TableCell className="font-medium">{item.vagas} vagas</TableCell>
                            <TableCell>{item.valor}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>Todas as funcionalidades</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>Filtros avançados</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="h-5 w-5 text-success flex-shrink-0" />
                      <span>Suporte JobwayX</span>
                    </li>
                  </ul>

                  <Separator />

                  <p className="text-sm text-muted-foreground italic">
                    A JobwayX organiza, prioriza e facilita a escolha dos melhores candidatos, reduzindo esforço do RH e tempo de contratação.
                  </p>

                  <Button 
                    className="w-full" 
                    size="lg" 
                    onClick={handleAssinaturaClick}
                    disabled={isLoading}
                  >
                    Assinar Plano
                  </Button>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </main>
      
      <Footer />

      <CreditosModal
        open={creditosModalOpen}
        onOpenChange={setCreditosModalOpen}
        onConfirm={handleCreditosConfirm}
        isLoading={isLoading}
      />

      <AssinaturaModal
        open={assinaturaModalOpen}
        onOpenChange={setAssinaturaModalOpen}
        onConfirm={handleAssinaturaConfirm}
        isLoading={isLoading}
      />
    </div>
  );
};

export default PlanosPage;
